// Vendor imports
const Immutable = require('immutable')
// Shared imports
const { TYPES: modelTypes } = require('shared/model-types')
// Client V2 common imports
const { keyedReviver } = require('client-v2/common/state/revivers')
// Model action imports
const articleModelActions = require('client-v2/common/models/article/actions')
// Model record imports
const ArticleModelRecord = require('client-v2/common/models/article/record')
const CollectionModelRecord = require('client-v2/common/models/collection/record')
const InterestModelRecord = require('client-v2/common/models/interest/record')
const { PageContextOverrideRecord } = require('client-v2/common/models/page-context/records')
const UserModelRecord = require('client-v2/common/models/user/record')
// Model reducer imports
const articleReducer = require('client-v2/common/models/article/reducer')
const interestReducer = require('client-v2/common/models/interest/reducer')
const collectionReducer = require('client-v2/common/models/collection/reducer')
const userReducer = require('client-v2/common/models/user/reducer')
// Sibling imports
const FeedItemMetaRecord = require('./item-meta')
const { wrapFeedItem } = require('../wrappers')

class FeedItemRecord extends Immutable.Record({
  article: null,
  collection: null,
  id: null,
  interest: null,
  meta: new FeedItemMetaRecord(),
  pageContext: null, // Only populated in denormalized form
  type: String(),
  user: null
}) {
  static fromJS(obj) {
    return Immutable.fromJS(obj, FeedItemRecord.reviver)
  }

  static fromModel(model) {
    const feedItem = wrapFeedItem(
      Immutable.Record.isRecord(model)
      ? model.toJS()
      : model
    )
    return FeedItemRecord.fromJS(feedItem)
  }

  getModel() {
    switch (this.type) {
      case modelTypes.ARTICLE: return this.article
      case modelTypes.INTEREST: return this.interest
      case modelTypes.MIX: return this.collection
      case modelTypes.USER: return this.user
    }
  }

  denormalize(streamMeta, pageContextOverlay = {}) {
    const itemPageContext = streamMeta.getPageContextForFeedItem(this)
    return this.set('pageContext', itemPageContext.merge(pageContextOverlay))
  }

  reducer(action) {
    switch (this.type) {
      case modelTypes.ARTICLE:
        if (
          action.type === articleModelActions.ARTICLE_RATED &&
          this.id === action.payload.urlId
        ) {
          return this.update('meta', meta => {
            return meta.set('rating', action.payload.rating)
          })
        }
        return this.update('article', article => {
          return articleReducer(article, action)
        })
      case modelTypes.INTEREST:
        return this.update('interest', interest => {
          return interestReducer(interest, action)
        })
      case modelTypes.MIX:
        return this.update('collection', collection => {
          return collectionReducer(collection, action)
        })
      case modelTypes.USER:
        return this.update('user', user => {
          return userReducer(user, action)
        })
      default:
        return this
    }
  }
}

FeedItemRecord.reviver = keyedReviver(FeedItemRecord, {
  article: ArticleModelRecord.reviver,
  collection: CollectionModelRecord.reviver,
  interest: InterestModelRecord.reviver,
  meta: FeedItemMetaRecord.reviver,
  pageContext: PageContextOverrideRecord.reviver,
  user: UserModelRecord.reviver
})

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  FeedItemRecord.propTypes = {
    article: PropTypes.shape(ArticleModelRecord.propTypes),
    collection: PropTypes.shape(CollectionModelRecord.propTypes),
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]).isRequired,
    interest: PropTypes.shape(InterestModelRecord.propTypes),
    meta: PropTypes.shape(FeedItemMetaRecord.propTypes),
    pageContext: PropTypes.shape(PageContextOverrideRecord.propTypes),
    type: PropTypes.oneOf(Object.values(modelTypes)).isRequired,
    user: PropTypes.shape(UserModelRecord.propTypes)
  }
}

module.exports = Object.assign(FeedItemRecord, {
  FeedItemMetaRecord
})
